/**
 * Weekly status report card (2026-09-27, upgraded 2026-09-28): generated from
 * the project's real tasks, RAID items, milestones and timeline.
 *
 *  - "What changed" compares with the last report that was sent.
 *  - Subscribe: get this report every Friday (read-only viewers too).
 *  - Share link: a no-login page with the report, for stakeholders without
 *    an account (revocable under the project's share links).
 */
import { useEffect, useState } from 'react'
import { Bell, BellOff, Link2, Mail, RefreshCw, Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import { useAuth } from '../../contexts/AuthContext'
import { statusReportService, type ReportSubscribers, type StatusReport } from '../../services/statusReport'
import { shareLinksService } from '../../services/shareLinks'
import { apiErrorMessage } from '../../utils/apiError'

const HEALTH_LABEL: Record<StatusReport['health'], string> = {
  on_track: 'On track',
  watch: 'Worth watching',
  at_risk: 'At risk',
}
const HEALTH_CLASS: Record<StatusReport['health'], string> = {
  on_track: 'bg-emerald-100 text-emerald-800',
  watch: 'bg-amber-100 text-amber-800',
  at_risk: 'bg-red-100 text-red-800',
}

export function StatusReportBody({ report }: { report: StatusReport }) {
  return (
    <div className="mt-4 space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${HEALTH_CLASS[report.health]}`}>{HEALTH_LABEL[report.health]}</span>
        <span className="text-muted-foreground">{report.progress_percent}% complete · {report.period_start} to {report.period_end}</span>
      </div>
      <p className="text-foreground">{report.summary}</p>
      {report.changes && (
        <div className="rounded-md border border-border bg-muted/40 p-3" aria-label="What changed">
          <h4 className="font-medium text-foreground">What changed since {report.changes.since}</h4>
          <ul className="list-disc pl-5 text-muted-foreground">
            {report.changes.lines.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h4 className="font-medium text-foreground">Highlights</h4>
          {report.highlights.length ? (
            <ul className="list-disc pl-5 text-muted-foreground">{report.highlights.map((h) => <li key={h}>{h}</li>)}</ul>
          ) : <p className="text-muted-foreground">Nothing finished this week.</p>}
        </div>
        <div>
          <h4 className="font-medium text-foreground">Watch</h4>
          {report.watch_items.length ? (
            <ul className="list-disc pl-5 text-muted-foreground">{report.watch_items.map((w) => <li key={w}>{w}</li>)}</ul>
          ) : <p className="text-muted-foreground">No overdue work or conflicts.</p>}
        </div>
      </div>
      <p className="text-muted-foreground">
        Due next {report.period_days} days: {report.due_next_period.length} · Open risks: {report.open_risks} · Open issues: {report.open_issues}
        {report.budget_spent_percent != null ? ` · Budget spent: ${report.budget_spent_percent}%` : ''}
        {report.forecast_finish ? ` · Forecast finish: ${report.forecast_finish}` : ''}
      </p>
    </div>
  )
}

export function WeeklyStatusCard({ projectId }: { projectId?: number }) {
  const { user } = useAuth()
  const [report, setReport] = useState<StatusReport | null>(null)
  const [subs, setSubs] = useState<ReportSubscribers | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!projectId) return
    statusReportService.subscribers(projectId).then(setSubs).catch(() => setSubs(null))
  }, [projectId])

  if (!projectId) return null

  const generate = async () => {
    setLoading(true)
    try {
      setReport(await statusReportService.preview(projectId))
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not build the status report. Try again.'))
    } finally {
      setLoading(false)
    }
  }

  const sendToMe = async () => {
    if (!user) return
    setSending(true)
    try {
      const res = await statusReportService.send(projectId, [user.id])
      if (res.sent_to.length) toast.success(`Status report emailed to ${user.email}`)
      else toast.error('The email could not be sent. Check the email settings.')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send the status report.'))
    } finally {
      setSending(false)
    }
  }

  const toggleSubscribe = async () => {
    setBusy(true)
    try {
      const next = await statusReportService.setSubscribed(projectId, !subs?.me_subscribed)
      setSubs(next)
      toast.success(next.me_subscribed ? 'You’ll get this report every Friday.' : 'Unsubscribed from the weekly report.')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update your subscription.'))
    } finally {
      setBusy(false)
    }
  }

  const shareLink = async () => {
    setBusy(true)
    try {
      const link = await shareLinksService.create(projectId, {
        label: 'Weekly status report',
        visibility_config: {
          show_status: true, show_milestones: true, show_budget_summary: false,
          show_flagged_risks: false, show_status_report: true,
        },
      })
      const url = `${window.location.origin}/share/${link.token}`
      try {
        await navigator.clipboard.writeText(url)
        toast.success('Link copied. Anyone with it can read this report; revoke it under Sharing.')
      } catch {
        toast.success(`Share link: ${url}`)
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not create a share link.'))
    } finally {
      setBusy(false)
    }
  }

  const others = (subs?.subscribers.length ?? 0) - (subs?.me_subscribed ? 1 : 0)

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Sparkles className="h-5 w-5 text-accent" /> Weekly status report
          </h3>
          <p className="text-sm text-muted-foreground">
            Written from this week’s tasks, risks, issues and milestones.
            {subs ? ` Sent every Friday to ${subs.subscribers.length} ${subs.subscribers.length === 1 ? 'person' : 'people'}.` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={generate} disabled={loading}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> {report ? 'Refresh' : 'Generate'}
          </button>
          {subs && (
            <button type="button" onClick={toggleSubscribe} disabled={busy} aria-pressed={subs.me_subscribed}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
              {subs.me_subscribed ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
              {subs.me_subscribed ? 'Unsubscribe' : 'Get it every Friday'}
            </button>
          )}
          <button type="button" onClick={shareLink} disabled={busy}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50">
            <Link2 className="h-4 w-4" /> Share report link
          </button>
          {report && (
            <button type="button" onClick={sendToMe} disabled={sending}
              className="flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50">
              <Mail className="h-4 w-4" /> {sending ? 'Sending…' : 'Email it to me'}
            </button>
          )}
        </div>
      </div>
      {subs && others > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Also receiving it: {subs.subscribers.filter((s) => s.user_id !== user?.id).map((s) => s.name).join(', ')}
        </p>
      )}

      {report && <StatusReportBody report={report} />}
    </div>
  )
}
